import React, { useEffect, useRef, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { FileText, Trash2, UploadCloud } from 'lucide-react';
import Button from '../../../shared/components/ui/Button';
import Modal from '../../../shared/components/ui/Modal';
import { Fold, TextCell, plural } from './reraUi';
import ReraMapDirections from './ReraMapDirections';
import { commitReraImport, parseReraDocument } from '../../services/api/implementation/impl';
import { buildCommitDraft } from '../../utils/reraLayout';
import {
  countDraft,
  findDraftProblems,
  removeBlock,
  removeLevel,
  removeUnit,
  renameAccommodation,
  renameBlock,
  renameLevel,
  renameUnit,
  setAccommodationSize
} from '../../utils/reraDraft';

const MAX_BYTES = 25 * 1024 * 1024;
const MAX_IMAGE_BYTES = 10 * 1024 * 1024;
const ACCEPTED_TYPES = ['application/pdf', 'image/png', 'image/jpeg', 'image/webp'];
const ACCEPTED_EXTENSIONS = /\.(pdf|png|jpe?g|webp)$/i;

const errorText = (err, fallback) => err?.response?.data?.message || err?.message || fallback;

const STEPS = ['Upload', 'Review', 'Done'];

const Stepper = ({ current }) => (
  <ol className="flex items-center gap-2 border-b border-hairline px-6 py-3 text-sm">
    {STEPS.map((label, i) => {
      const state = i < current ? 'done' : i === current ? 'active' : 'todo';
      return (
        <li key={label} className="flex items-center gap-2">
          <span
            className={`flex h-6 w-6 items-center justify-center rounded-full text-xs font-semibold ${
              state === 'todo' ? 'bg-gray-100 text-gray-500' : 'bg-brand-700 text-white'
            }`}>
            {state === 'done' ? '✓' : i + 1}
          </span>
          <span className={state === 'active' ? 'font-semibold text-brand-800' : 'text-gray-500'}>{label}</span>
          {i < STEPS.length - 1 && <span className="mx-1 h-px w-8 bg-gray-200" />}
        </li>
      );
    })}
  </ol>
);

const toSize = (text) => {
  const n = Number(text);
  return text.trim() !== '' && Number.isFinite(n) && n > 0 ? n : null;
};

/** Keeps what the user typed ("12." on the way to "12.19") while the draft holds the parsed number. */
const NumberCell = ({ value, onChange, label }) => {
  const [text, setText] = useState(value == null ? '' : String(value));
  useEffect(() => {
    if (toSize(text) !== (value ?? null)) setText(value == null ? '' : String(value));
  }, [value]);
  return (
    <TextCell
      label={label}
      value={text}
      onChange={(t) => {
        setText(t);
        onChange(t);
      }}
    />
  );
};

const UploadStep = ({ onParsed, projectId }) => {
  const inputRef = useRef(null);
  const [file, setFile] = useState(null);
  const [dragOver, setDragOver] = useState(false);

  const parse = useMutation({
    mutationFn: () => parseReraDocument(projectId, file),
    onSuccess: (data) => onParsed(data.draft),
    onError: (err) => toast.error(errorText(err, 'Could not read this document'))
  });

  const pick = (f) => {
    if (!f) return;
    if (!ACCEPTED_TYPES.includes(f.type) && !ACCEPTED_EXTENSIONS.test(f.name)) {
      toast.error('Please choose a PDF, PNG, JPG or WebP file');
      return;
    }
    if (f.size > MAX_BYTES) {
      toast.error('The file is larger than 25MB');
      return;
    }
    const isImage = f.type.startsWith('image/') || /\.(png|jpe?g|webp)$/i.test(f.name);
    if (isImage && f.size > MAX_IMAGE_BYTES) {
      toast.error('Images can be up to 10MB. Export the map at a smaller size (about 5000px wide is enough) or upload the PDF.');
      return;
    }
    setFile(f);
  };

  return (
    <div className="flex flex-col gap-4 p-6">
      <p className="text-sm text-gray-700">
        Upload the RERA unit sheet or plot area statement, or a picture of the layout map. We read the blocks, levels, unit numbers and areas, and you
        review everything before anything is created.
      </p>
      <div
        onDragOver={(e) => {
          e.preventDefault();
          setDragOver(true);
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDragOver(false);
          pick(e.dataTransfer.files?.[0]);
        }}
        className={`flex flex-col items-center gap-3 rounded-lg border-2 border-dashed px-6 py-10 text-center ${
          dragOver ? 'border-brand-600 bg-brand-50' : 'border-brand-100 bg-white'
        }`}>
        {file ? <FileText className="h-8 w-8 text-brand-700" /> : <UploadCloud className="h-8 w-8 text-brand-700" />}
        <div className="text-sm font-medium">{file ? file.name : 'Drag a PDF or map image here, or choose a file'}</div>
        <div className="text-xs text-gray-500">PDF up to 25MB, or a PNG / JPG / WebP picture up to 10MB. A picture is read as a layout map, so use a clear one with the plot numbers visible.</div>
        <input
          ref={inputRef}
          type="file"
          accept="application/pdf,image/png,image/jpeg,image/webp,.pdf,.png,.jpg,.jpeg,.webp"
          className="hidden"
          data-testid="rera-file-input"
          onChange={(e) => pick(e.target.files?.[0])}
        />
        <Button variant="secondary" size="sm" onClick={() => inputRef.current?.click()}>
          {file ? 'Choose a different file' : 'Choose file'}
        </Button>
      </div>
      <div className="flex justify-end">
        <Button variant="primary" disabled={!file || parse.isPending} onClick={() => parse.mutate()}>
          {parse.isPending ? 'Reading…' : 'Read document'}
        </Button>
      </div>
    </div>
  );
};

const PreviewStep = ({ draft, setDraft, projectId, onDone, onBack }) => {
  const counts = countDraft(draft);
  const problems = findDraftProblems(draft);
  const warnings = draft.warnings || [];
  const hasPlotTypes = draft.accommodations.some((a) => a.kind === 'independent');
  const unitWord = hasPlotTypes && !draft.accommodations.some((a) => a.kind === 'group') ? 'plot' : 'unit';
  const isShort = counts.units <= 40;

  const commit = useMutation({
    mutationFn: () => commitReraImport(projectId, buildCommitDraft(draft)),
    onSuccess: (summary) => {
      toast.success('RERA data imported');
      onDone(summary);
    },
    onError: (err) => toast.error(errorText(err, 'Import failed'))
  });

  return (
    <div className="flex max-h-[78vh] flex-col">
      <div className="flex-1 space-y-4 overflow-y-auto p-6">
        <div>
          <h3 className="text-lg font-semibold text-brand-800">
            We found {plural(counts.units, unitWord, `${unitWord}s`)} in {plural(counts.blocks, 'block', 'blocks')}
          </h3>
          <p className="mt-1 text-sm text-gray-600">
            Nothing is created yet. Look through the details below, fix anything that is wrong, then press{' '}
            <span className="font-medium">Create</span> at the bottom.
          </p>
        </div>

        {problems.length > 0 && (
          <div className="rounded-lg border border-danger-border bg-danger-bg px-4 py-3 text-sm text-danger-fg">
            <div className="font-medium">Fix these before you can continue</div>
            <ul className="mt-1 list-disc pl-5">
              {problems.slice(0, 6).map((p) => (
                <li key={p}>{p}</li>
              ))}
            </ul>
          </div>
        )}

        {warnings.length > 0 && (
          <div className="rounded-lg border border-warning-border bg-warning-bg px-4 py-3 text-sm text-warning-fg">
            <div className="font-medium">Please double-check</div>
            <ul className="mt-1 list-disc pl-5">
              {warnings.slice(0, 5).map((w) => (
                <li key={w}>{w}</li>
              ))}
              {warnings.length > 5 && <li>and {warnings.length - 5} more</li>}
            </ul>
          </div>
        )}

        <Fold
          title="Accommodation types"
          hint={`${draft.accommodations.length} found. A type groups units of the same size, like "3 BHK" or a 111 sq m plot.`}
          defaultOpen>
          <div className="max-h-60 overflow-y-auto rounded-lg border border-hairline">
            <table className="w-full text-sm">
              <thead className="sticky top-0 bg-surface-muted text-left text-xs text-gray-600">
                <tr>
                  <th className="px-3 py-2">Name</th>
                  <th className="px-3 py-2">Kind</th>
                  <th className="px-3 py-2">Area (sq m)</th>
                  {hasPlotTypes && <th className="px-3 py-2">Width (mt)</th>}
                  {hasPlotTypes && <th className="px-3 py-2">Depth (mt)</th>}
                </tr>
              </thead>
              <tbody>
                {draft.accommodations.map((a) => (
                  <tr key={a.key} className="border-t border-hairline">
                    <td className="px-3 py-1.5">
                      <TextCell
                        label={`Accommodation ${a.key}`}
                        value={a.name}
                        onChange={(v) => setDraft(renameAccommodation(draft, a.key, v))}
                      />
                    </td>
                    <td className="px-3 py-1.5">
                      {a.kind === 'group' ? 'Apartment' : a.plotType === 'regular' ? 'Plot' : 'Plot (irregular)'}
                    </td>
                    <td className="px-3 py-1.5">{a.carpetArea ?? a.plotArea}</td>
                    {hasPlotTypes && (
                      <td className="px-3 py-1.5">
                        {a.kind === 'independent' ? (
                          <NumberCell
                            label={`Width of ${a.name}`}
                            value={a.width ?? null}
                            onChange={(v) => setDraft(setAccommodationSize(draft, a.key, 'width', v))}
                          />
                        ) : null}
                      </td>
                    )}
                    {hasPlotTypes && (
                      <td className="px-3 py-1.5">
                        {a.kind === 'independent' ? (
                          <NumberCell
                            label={`Depth of ${a.name}`}
                            value={a.length ?? null}
                            onChange={(v) => setDraft(setAccommodationSize(draft, a.key, 'length', v))}
                          />
                        ) : null}
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {hasPlotTypes && (
            <p className="mt-2 text-xs text-gray-500">
              Width is the side along the road and depth is how far the plot goes back. Leave both empty for plots
              that are not a simple rectangle.
            </p>
          )}
        </Fold>
        {draft.blocks.map((b, bi) => {
          const blockUnits = b.levels.reduce((n, l) => n + l.units.length, 0);
          return (
            <Fold
              key={bi}
              title={`Block: ${b.name || '(no name)'}`}
              hint={`${plural(b.levels.length, 'level', 'levels')} · ${plural(blockUnits, 'unit', 'units')}`}
              defaultOpen>
              <div className="mb-3 flex flex-wrap items-center gap-3">
                <span className="text-xs font-semibold uppercase tracking-wide text-brand-700">Block name</span>
                <div className="w-60">
                  <TextCell
                    label={`Block ${bi + 1} name`}
                    value={b.name}
                    onChange={(v) => setDraft(renameBlock(draft, bi, v))}
                  />
                </div>
                <button
                  type="button"
                  aria-label={`Remove block ${b.name}`}
                  className="ml-auto flex items-center gap-1 text-xs text-danger hover:opacity-80"
                  onClick={() => setDraft(removeBlock(draft, bi))}>
                  <Trash2 className="h-4 w-4" /> Remove block
                </button>
              </div>
              <div className="space-y-3">
                {b.levels.map((l, li) => (
                  <Fold
                    key={li}
                    title={`Level: ${l.name || '(no name)'}`}
                    hint={plural(l.units.length, 'unit', 'units')}
                    defaultOpen={isShort}>
                    <div className="mb-3 flex items-center gap-2">
                      <span className="text-xs font-semibold uppercase tracking-wide text-gray-500">Level name</span>
                      <div className="w-48">
                        <TextCell
                          label={`Level ${li + 1} name in ${b.name}`}
                          value={l.name}
                          onChange={(v) => setDraft(renameLevel(draft, bi, li, v))}
                        />
                      </div>
                      <button
                        type="button"
                        aria-label={`Remove level ${l.name}`}
                        className="ml-auto flex items-center gap-1 text-xs text-danger hover:opacity-80"
                        onClick={() => setDraft(removeLevel(draft, bi, li))}>
                        <Trash2 className="h-3.5 w-3.5" /> Remove level
                      </button>
                    </div>
                    <p className="mb-2 text-xs text-gray-500">
                      Each box is a unit number. The grey number beside it is its area in sq m.
                    </p>
                    <div className="grid max-h-72 grid-cols-2 gap-x-3 gap-y-1 overflow-y-auto md:grid-cols-3">
                      {l.units.map((u, ui) => (
                        <div key={ui} className="flex items-center gap-1">
                          <TextCell
                            label={`Unit ${u.unit_name}`}
                            value={u.unit_name}
                            onChange={(v) => setDraft(renameUnit(draft, bi, li, ui, v))}
                          />
                          <span className="w-12 shrink-0 text-right text-xs text-gray-500">{u.area}</span>
                          <button
                            type="button"
                            aria-label={`Remove unit ${u.unit_name}`}
                            className="text-gray-400 hover:text-danger"
                            onClick={() => setDraft(removeUnit(draft, bi, li, ui))}>
                            <Trash2 className="h-3.5 w-3.5" />
                          </button>
                        </div>
                      ))}
                    </div>
                  </Fold>
                ))}
              </div>
            </Fold>
          );
        })}

        <ReraMapDirections draft={draft} setDraft={setDraft} />
      </div>

      <div className="flex items-center justify-between border-t border-hairline px-6 py-3">
        <Button variant="ghost" onClick={onBack} disabled={commit.isPending}>
          Choose another file
        </Button>
        <Button
          variant="primary"
          disabled={problems.length > 0 || commit.isPending}
          onClick={() => commit.mutate()}>
          {commit.isPending ? 'Creating…' : `Create ${counts.units} units`}
        </Button>
      </div>
    </div>
  );
};

const DoneStep = ({ summary, onClose }) => {
  const row = (label, created, extra) => (
    <li className="flex items-center justify-between rounded-lg border border-hairline px-4 py-2 text-sm">
      <span className="text-gray-700">{label}</span>
      <span>
        <span className="font-semibold text-brand-800">{created} added</span>
        {extra > 0 && <span className="ml-2 text-xs text-gray-500">{extra} already there</span>}
      </span>
    </li>
  );
  return (
    <div className="flex flex-col gap-4 p-6">
      <div className="text-lg font-semibold text-brand-800">Import finished</div>
      <ul className="space-y-2">
        {row('Accommodation types', summary.accommodations.created, summary.accommodations.existing)}
        {row('Blocks', summary.blocks.created, summary.blocks.existing)}
        {row('Levels', summary.levels.created, summary.levels.existing)}
        {row('Units', summary.units.created, summary.units.skipped)}
      </ul>
      {summary.layout && (
        <ul className="space-y-2">
          <li className="flex items-center justify-between rounded-lg border border-hairline px-4 py-2 text-sm">
            <span className="text-gray-700">Map on the Layout tab</span>
            <span className="font-semibold text-brand-800">
              {summary.layout.saved ? `${summary.layout.plots} plots outlined` : 'not saved'}
            </span>
          </li>
          {summary.directions && (
            <li className="flex items-center justify-between rounded-lg border border-hairline px-4 py-2 text-sm">
              <span className="text-gray-700">Directions to units</span>
              <span className="font-semibold text-brand-800">
                {summary.directions.units} units: {summary.directions.facing} facings, {summary.directions.items} side items
              </span>
            </li>
          )}
        </ul>
      )}
      {summary.layout?.unmatched?.length > 0 && (
        <p className="text-xs text-gray-500">
          Outlines with no unit, left off the map: {summary.layout.unmatched.slice(0, 10).join(', ')}
        </p>
      )}
      {summary.skippedUnits?.length > 0 && (
        <p className="text-xs text-gray-500">
          Skipped because they already exist: {summary.skippedUnits.slice(0, 10).join(', ')}
          {summary.skippedUnits.length > 10 ? '…' : ''}
        </p>
      )}
      <div className="flex justify-end">
        <Button variant="primary" onClick={onClose}>
          Close
        </Button>
      </div>
    </div>
  );
};

/**
 * Upload a RERA PDF, review and edit the draft, then create the accommodation
 * types, blocks, floors and units for the project in one go.
 */
const ImportReraModal = ({ projectId, onClose, onImported }) => {
  const [draft, setDraft] = useState(null);
  const [summary, setSummary] = useState(null);

  const handleClose = () => {
    if (summary) onImported?.();
    onClose();
  };

  return (
    <Modal
      name="Import from RERA document"
      width={72}
      isDirty={Boolean(draft) && !summary}
      confirmOnClose
      onClose={handleClose}>
      <Stepper current={summary ? 2 : draft ? 1 : 0} />
      {summary ? (
        <DoneStep summary={summary} onClose={handleClose} />
      ) : draft ? (
        <PreviewStep
          draft={draft}
          setDraft={setDraft}
          projectId={projectId}
          onBack={() => setDraft(null)}
          onDone={setSummary}
        />
      ) : (
        <UploadStep projectId={projectId} onParsed={setDraft} />
      )}
    </Modal>
  );
};

export default ImportReraModal;
